import { forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import { format, parse } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import DateIcon from '@/shared/assets/icons/date.svg';
import { Svg } from '@/shared/ui/Svg';
import { FieldWrapper } from '../FieldWrapper';
import { BaseFieldProps, getFieldWithIconStyles } from '../BaseField';
export interface DateSelectFieldProps extends BaseFieldProps {
  value?: string; // ISO date string (YYYY-MM-DD)
  onChange?: (name: string, dateString: string) => void;
  minDate?: Date;
  maxDate?: Date;
  showTimeSelect?: boolean;
  timeFormat?: string;
  dateFormat?: string;
  /** Списки месяца и года в шапке календаря: дату рождения стрелками по месяцу не выбрать */
  showMonthYearDropdowns?: boolean;
}

export const DateSelectField = forwardRef<DatePicker, DateSelectFieldProps>((props, ref) => {
  const {
    name,
    label,
    error,
    showError = false,
    labelClassName,
    disabled,
    value,
    onChange,
    minDate,
    maxDate,
    showTimeSelect = false,
    timeFormat = 'HH:mm',
    dateFormat = 'dd/MM/yyyy',
    showMonthYearDropdowns = false,
    ...rest
  } = props;

  // Календарь отдаёт локальную полночь: через toISOString в поясах восточнее UTC
  // выбранный день уезжал на предыдущий (15 мая → 2026-05-14)
  const handleDateChange = (date: Date | null) => {
    const dateString = date ? format(date, 'yyyy-MM-dd') : '';
    onChange?.(name, dateString);
  };

  // YYYY-MM-DD — календарная дата, а не полночь UTC: иначе западнее UTC показывался бы
  // предыдущий день
  const selectedDate = value
    ? /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? parse(value, 'yyyy-MM-dd', new Date())
      : new Date(value)
    : null;

  return (
    <FieldWrapper
      label={label}
      error={error}
      showError={showError}
      fieldId={name}
      labelClassName={labelClassName}
    >
      <div className="relative">
        <DatePicker
          ref={ref}
          selected={selectedDate}
          onChange={handleDateChange}
          minDate={minDate}
          maxDate={maxDate}
          showTimeSelect={showTimeSelect}
          timeFormat={timeFormat}
          dateFormat={dateFormat}
          showMonthDropdown={showMonthYearDropdowns}
          showYearDropdown={showMonthYearDropdowns}
          dropdownMode="select"
          disabled={disabled}
          // customInput клонируется со своими пропсами, поэтому id и aria-атрибуты
          // задаём здесь: иначе подпись <label for> ссылается в пустоту
          id={name}
          ariaInvalid={showError && error ? 'true' : undefined}
          ariaDescribedBy={showError && error ? `${name}-error` : undefined}
          placeholderText="Select date"
          wrapperClassName="w-full"
          autoComplete="off"
          customInput={
            <input
              className={getFieldWithIconStyles(error, undefined, disabled)}
              placeholder="Select date"
              disabled={disabled}
              readOnly
            />
          }
          {...rest}
        />
        <Svg
          Icon={DateIcon}
          className="absolute right-[14px] top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none"
        />
      </div>
    </FieldWrapper>
  );
});

DateSelectField.displayName = 'DateSelectField';
